import { RefObject, useEffect, useRef } from "react";
import type { WheelState } from "./RaffleWheel";

// Скорость неспешного вращения в ожидании розыгрыша.
const IDLE_DEG_PER_MS = 96 / 1000;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// idle — крутится постоянно; spinning — равномерно тормозит с текущей
// скорости до нуля за spinMs; stopped — стоит, где остановилось.
// Угол меняем напрямую в DOM, без ре-рендеров на каждый кадр.
export const useWheelRotation = (
  ref: RefObject<SVGGElement>,
  state: WheelState,
  spinMs: number,
) => {
  const angleRef = useRef(0);

  useEffect(() => {
    const element = ref.current;
    if (!element || state === "stopped" || prefersReducedMotion()) return;

    const startedAt = performance.now();
    const fromAngle = angleRef.current;
    let lastAt = startedAt;
    let frame = 0;

    const tick = (now: number) => {
      if (state === "idle") {
        angleRef.current += IDLE_DEG_PER_MS * (now - lastAt);
      } else {
        // Путь при равнозамедленном движении: v0·t − v0·t²/(2T).
        const t = Math.min(now - startedAt, spinMs);
        angleRef.current =
          fromAngle + IDLE_DEG_PER_MS * (t - (t * t) / (2 * spinMs));
      }
      lastAt = now;
      element.style.transform = `rotate(${angleRef.current % 360}deg)`;
      if (state === "idle" || now - startedAt < spinMs) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [ref, state, spinMs]);
};
